import { CalendarIcon } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Drawer, DrawerClose, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TimeInput } from "@/components/ui/time-input";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { TimezoneService } from "@/lib/timezone";
import { cn } from "@/lib/utils";

interface ActivePeriodSectionProps {
  startDateTime: DateTime;
  onStartDateTimeChange: (dateTime: DateTime) => void;
  endDateTime: DateTime;
  onEndDateTimeChange: (dateTime: DateTime) => void;
  disabled?: boolean;
  title?: string;
  hasError?: boolean;
}

/**
 * Renders a calendar picker that uses Popover on desktop and Drawer on mobile.
 * Safari iOS breaks Popover positioning inside translated Dialog containers.
 */
function DatePicker({
  value,
  onChange,
  disabled,
  hasError,
  placeholder,
}: {
  value: DateTime;
  onChange: (date: Date | undefined) => void;
  disabled: boolean;
  hasError: boolean;
  placeholder: string;
}) {
  const { t } = useTranslation("discounts");
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const selectedDate = value ? new Date(value.year, value.month - 1, value.day) : undefined;

  const handleSelect = (date: Date | undefined) => {
    onChange(date);
    setOpen(false);
  };

  const trigger = (
    <Button
      variant="outline"
      className={cn(
        "w-full pl-3 text-left font-normal",
        !value && "text-muted-foreground",
        hasError && "border-destructive"
      )}
      disabled={disabled}
    >
      {value ? value.toFormat("MMM dd, yyyy") : <span>{placeholder}</span>}
      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
    </Button>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className="h-[60vh] flex flex-col">
          <div className="flex-1 p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleSelect}
              disabled={disabled}
              className="w-full [--cell-size:2.75rem]"
            />
          </div>
          <div className="px-4 pb-4 pt-2 border-t mt-auto">
            <DrawerClose asChild>
              <Button variant="outline" className="w-full">
                {t("common:actions.cancel")}
              </Button>
            </DrawerClose>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="!w-[350px] p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          disabled={disabled}
          initialFocus
          className="w-full [--cell-size:2.5rem]"
        />
      </PopoverContent>
    </Popover>
  );
}

export function ActivePeriodSection({
  startDateTime,
  onStartDateTimeChange,
  endDateTime,
  onEndDateTimeChange,
  disabled = false,
  title,
  hasError = false,
}: ActivePeriodSectionProps) {
  const { t } = useTranslation("discounts");

  const handleStartDateChange = (date: Date | undefined) => {
    if (!date) return;
    const newStartDate = DateTime.fromObject(
      { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() },
      { zone: TimezoneService.getCurrentSalonTimezone() }
    );
    onStartDateTimeChange(
      newStartDate.set({
        hour: startDateTime.hour,
        minute: startDateTime.minute,
        second: 0,
        millisecond: 0,
      })
    );
  };

  const handleStartTimeChange = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    onStartDateTimeChange(
      startDateTime.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
    );
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (!date) return;
    const newEndDate = DateTime.fromObject(
      { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() },
      { zone: TimezoneService.getCurrentSalonTimezone() }
    );
    onEndDateTimeChange(
      newEndDate.set({
        hour: endDateTime.hour,
        minute: endDateTime.minute,
        second: 0,
        millisecond: 0,
      })
    );
  };

  const handleEndTimeChange = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    onEndDateTimeChange(
      endDateTime.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
    );
  };

  return (
    <div className={cn("space-y-4", hasError && "ring-2 ring-destructive rounded-lg p-4")}>
      {title && <label className="text-sm font-medium">{title}</label>}

      {/* Start DateTime */}
      <div className="space-y-2">
        <label className="text-sm text-muted-foreground">{t("form.activePeriod.startDate")}</label>
        <div className="grid grid-cols-2 gap-4">
          <DatePicker
            value={startDateTime}
            onChange={handleStartDateChange}
            disabled={disabled}
            hasError={hasError}
            placeholder={t("form.activePeriod.selectDate")}
          />
          <TimeInput
            value={startDateTime.toFormat("HH:mm")}
            onChange={handleStartTimeChange}
            className={cn(hasError && "border-destructive")}
            disabled={disabled}
          />
        </div>
      </div>

      {/* End DateTime */}
      <div className="space-y-2">
        <label className="text-sm text-muted-foreground">{t("form.activePeriod.endDate")}</label>
        <div className="grid grid-cols-2 gap-4">
          <DatePicker
            value={endDateTime}
            onChange={handleEndDateChange}
            disabled={disabled}
            hasError={hasError}
            placeholder={t("form.activePeriod.selectDate")}
          />
          <TimeInput
            value={endDateTime.toFormat("HH:mm")}
            onChange={handleEndTimeChange}
            className={cn(hasError && "border-destructive")}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}
